/* eslint-disable camelcase */

import type { RGBColor } from "react-color";

import type { BeePluginFont } from "@beefree.io/sdk/dist/types/bee";

export const STYLE_SETTINGS_KEY = "styleSettings";

export const styleTypes = [
  "basicStyling",
  "titleStyling",
  "bodyParagraphStyling",
  "bodyListStyling",
  "buttonStyling",
  "fallbackFonts",
  "genericFallbackFont",
  "emailTemplateWidth",
  "contentBlockWidth",
  "iconBlockStyling",
] as const;
export type StyleType = (typeof styleTypes)[number];

export const styleGroupKeys = ["headerStyling", "blockStyling"] as const;
export type StyleGroupKey = (typeof styleGroupKeys)[number];

export const styleKeys = [
  "borderColor",
  "borderRadius",
  "borderWeight",
  "backgroundColor",
  "contentAreaBackgroundColor",
  "emailBackgroundColor",
  "font",
  "fontColor",
  "fontSize",
  "fontWeight",
  "fontWeight",
  "letterSpacing",
  "lineHeight",
  "linkColor",
  "listItemIndent",
  "listItemSpacing",
  "listStyleType",
  "listType",
  "padding",
  "paragraphSpacing",
  "startListFrom",
  "textAlign",
  "textDirection",
];
export type StyleKey = (typeof styleKeys)[number];

export const headerLevels = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;
export type HeaderLevel = (typeof headerLevels)[number];

export type Font = Omit<BeePluginFont, "fontWeight"> & {
  fontWeight?: Record<number, string>;
};

export type FontWeight = 400 | 700;

export type Align = "left" | "center" | "right";
export type TextAlign = "left" | "center" | "right";

export type TextDirection = "ltr" | "rtl";

export type ListType = "ul" | "ol";

export type ListStyleType =
  | "revert"
  | "auto"
  | "disc"
  | "circle"
  | "square"
  | "decimal"
  | "lower-alpha"
  | "upper-alpha"
  | "lower-roman"
  | "upper-roman";

export type Padding = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export interface BasicStyling {
  emailBackgroundColor: RGBColor;
  contentAreaBackgroundColor: RGBColor;
  font: Font;
  linkColor: RGBColor;
}

export interface TitleHeaderStyling {
  fontSize: number;
  fontColor: RGBColor;
  textAlign: TextAlign;
  textDirection: TextDirection;
  font?: Font;
  linkColor?: RGBColor;
}

export interface BlockStyling {
  padding: Padding;
}

export interface TitleStyling {
  headerStyling: {
    [headerLevel in HeaderLevel]?: TitleHeaderStyling;
  };
  blockStyling: BlockStyling;
}

export interface BodyParagraphStyling {
  fontSize: number;
  fontColor: RGBColor;
  blockStyling: BlockStyling;
  font?: Font;
  linkColor?: RGBColor;
  fontWeight: FontWeight;
  lineHeight: number | null;
  textAlign: TextAlign;
  textDirection: TextDirection;
  letterSpacing: number;
  paragraphSpacing: number;
}

export interface BodyListStyling {
  fontSize: number;
  fontColor: RGBColor;
  blockStyling: BlockStyling;
  font?: Font;
  linkColor?: RGBColor;
  fontWeight: FontWeight;
  lineHeight: number | null;
  letterSpacing: number;
  listItemSpacing: number;
  listItemIndent: number;
  listStyleType: ListStyleType;
  listType: ListType;
  startListFrom: number;
  textAlign: TextAlign;
  textDirection: TextDirection;
}

export interface ButtonStyling {
  backgroundColor: RGBColor;
  fontSize: number;
  fontColor: RGBColor;
  borderRadius: number;
  borderColor: RGBColor;
  borderWeight: number;
  padding: Padding;
  blockStyling: BlockStyling;
  font?: Font;
}

export type TemplateWidthSetting = { width: number; isFullWidth?: boolean };

export type IconHeightEnum = "16px" | "32px" | "48px" | "64px" | "128px";
export type ItemSpacingEnum = "0" | "5" | "10" | "15" | "20";

export interface IconBlockStyling {
  fontColor: RGBColor;
  fontSize: number;
  fontFamily: string;
  fontWeight: FontWeight;
  itemSpacing: ItemSpacingEnum;
  iconHeight: IconHeightEnum;
  iconSpacing: Padding;
  blockStyling: BlockStyling & { align: TextAlign };
  mobileStyles: {
    padding: Padding;
    textAlign: TextAlign;
    fontSize: number;
  };
}

export interface GlobalStyleSettings {
  basicStyling: BasicStyling;
  titleStyling: TitleStyling;
  bodyParagraphStyling: BodyParagraphStyling;
  bodyListStyling: BodyListStyling;
  buttonStyling: ButtonStyling;
  fallbackFonts: Font[];
  genericFallbackFont: Font;
  emailTemplateWidth: TemplateWidthSetting;
  contentBlockWidth: TemplateWidthSetting;
  iconBlockStyling: IconBlockStyling;
}

export interface DragAndDropEditorSettings {
  [STYLE_SETTINGS_KEY]: GlobalStyleSettings | {};
}

export interface PostDragAndDropEditorSettingsResult {
  success: boolean;
}

export interface PostDragAndDropEditorSettingsProps {
  appGroupId: string;
  /** The new style settings to persist. */
  style_settings: GlobalStyleSettings;
}
